import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/config/axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Normalized current user id (supports both `id` and `_id`)
  const getUserId = () => user?.id || user?._id;

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title, options = {}) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    try {
      setLoading(true);
      const response = await api.get('/api/notifications', {
        params: { limit: 50 }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch unread count only
  const fetchUnreadCount = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    try {
      const response = await api.get('/api/notifications/unread-count');
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await api.put(`/api/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId
            ? { ...n, read: true, readAt: new Date() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.put('/api/notifications/read-all');
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, readAt: new Date() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await api.delete(`/api/notifications/${notificationId}`);
      const notification = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [notifications]);

  // Add new notification (for real-time updates)
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.read) {
      setUnreadCount(prev => prev + 1);
      // Show browser notification
      showBrowserNotification(notification.title, {
        body: notification.message,
        tag: notification._id
      });
    }
  }, [showBrowserNotification]);

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      let previousUnreadCount = 0;
      const interval = setInterval(async () => {
        try {
          const response = await api.get('/api/notifications/unread-count');
          const currentUnreadCount = response.data.unreadCount || 0;
          
          // If unread count increased, fetch full notifications and show browser notification
          if (currentUnreadCount > previousUnreadCount) {
            const notificationsResponse = await api.get('/api/notifications', {
              params: { limit: 50 }
            });
            const newNotifications = notificationsResponse.data.notifications || [];
            
            // Get current notifications to compare
            setNotifications(prevNotifications => {
              // Find the new unread notifications
              const newUnreadNotifications = newNotifications.filter(n => !n.read);
              const existingIds = new Set(prevNotifications.map(n => n._id));
              const trulyNewNotifications = newUnreadNotifications.filter(n => !existingIds.has(n._id));
              
              // Show browser notification for each new notification
              trulyNewNotifications.forEach(notification => {
                showBrowserNotification(notification.title, {
                  body: notification.message,
                  tag: notification._id,
                  icon: '/favicon.ico',
                  badge: '/favicon.ico'
                });
              });
              
              return newNotifications;
            });
            
            setUnreadCount(currentUnreadCount);
          } else {
            // Just update the count if it didn't increase
            setUnreadCount(currentUnreadCount);
          }
          
          previousUnreadCount = currentUnreadCount;
        } catch (error) {
          console.error('Error polling notifications:', error);
        }
      }, 30000);

      return () => clearInterval(interval);
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, fetchNotifications, showBrowserNotification]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
    showBrowserNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
